import { parse } from 'qs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, RouteComponentProps } from 'react-router';
import { Switch } from 'react-router-dom';

import { Cart } from 'src/components/Cart/Cart';
import { Checkout } from 'src/components/Checkout/Checkout';
import { ChoosePartner } from 'src/components/ChoosePartner/ChoosePartner';
import { NotFoundPage } from 'src/components/ErrorPage/NotFoundPage';
import { PartnerBump } from 'src/components/PartnerBump/PartnerBump';
import { PublicSimplePage } from 'src/components/PublicSimplePage/PublicSimplePage';
import { useSalesChannel } from 'src/components/SalesChannel/SalesChannel';
import { redesignV2RoutePath } from 'src/constants/redesignV2';
import { getBaseUrlFromLocale, useRoutesByDataCenter } from 'src/utils/AppRoute';
import { getField } from 'src/utils/EntryUtil';
import { Environment } from 'src/utils/Environment';
import { ALL_LOCALES_FOR_DATA_CENTER, DEFAULT_LOCALE } from 'src/utils/Locales';
import { enableInternationalRedirect } from 'src/utils/Region';
import { ensureNoTrailingSlash } from 'src/utils/Urls';

import { PagesSwitch } from './PagesSwitch/PagesSwitch';
import { useSpeedbumpEntry } from './UseSpeedbumpEntry';

export const MAIN_LEAD_GEN_FORM = '/contact/request-a-call';
export const REQUEST_A_DEMO_PATH = '/contact/request-a-demo';
export const CONNECT_TO_SALES_PATH = '/contact/connect-to-sales';

export const Routes: React.FC<{children?: never}> = () => {
  const { i18n } = useTranslation();
  const [routeToLocale] = useRoutesByDataCenter(ALL_LOCALES_FOR_DATA_CENTER);
  const { speedbumpEntry } = useSpeedbumpEntry(i18n.language);
  const { salesChannel } = useSalesChannel();
  const { protocol, hostname } = window.location;

  const isDemoAvailable = getField(salesChannel, 'isDemoAvailable');
  const externalBankContactLink = Environment.getExternalBankContactLink(getField(salesChannel, 'referralId'));

  return (
    <Switch>
      {!Environment.current.isCloverProdEnvironment && <Route exact path="/partner" render={() => <ChoosePartner entry={speedbumpEntry} />} />}
      <Route path="/partner/:partnerName" render={() => <PartnerBump />} />
      <Route path={redesignV2RoutePath.PURCHASE} component={Environment.current.isExistingMerchant ? () => <Redirect to={MAIN_LEAD_GEN_FORM} /> : Checkout} />
      <Route path={redesignV2RoutePath.CART} component={Environment.current.isExistingMerchant ? () => <Redirect to={MAIN_LEAD_GEN_FORM} /> : Cart} />

      {externalBankContactLink && (
        <Route
          exact
          path={[MAIN_LEAD_GEN_FORM, REQUEST_A_DEMO_PATH, CONNECT_TO_SALES_PATH]}
          render={() => {
            window.location.replace(externalBankContactLink);
            return null;
          }}
        />
      )}

      {isDemoAvailable === false && (
        <Route
          exact
          path={REQUEST_A_DEMO_PATH}
          render={() => {
            window.location.replace(`${protocol}//${hostname}${MAIN_LEAD_GEN_FORM}`);
            return null;
          }}
        />
      )}

      <Route
        render={() => (
          <>
            <PagesSwitch>
              {/* ye-old-public-space */}
              <Route exact path="/terms" render={() => <PublicSimplePage title="Terms | Clover" entryId="NQ6x0TCraEQaQmUgACUS8" />} />
              <Route exact path="/privacy-policy" render={() => <PublicSimplePage title="Privacy Policy | Clover" entryId="7b1EbFbQpUi6sE2oCk0KSi" />} />
              <Route exact path="/app-market-terms" render={() => <PublicSimplePage title="App Market Terms | Clover" entryId="3AlHJMvIukqY2CqqYA4e2Q" />} />
              <Route exact path="/app-market-policies" render={() => <PublicSimplePage title="App Market Policies | Clover" entryId="6SWPBAyMvYEEqsS4gKecKi" />} />
              <Route exact path="/developer-agreement" render={() => <PublicSimplePage title="Developer Agreement | Clover" entryId="5dNCkmcLSESUmiYg2SUaY2" />} />
              <Route exact path="/legal-guidelines" render={() => <PublicSimplePage title="Legal Guidelines | Clover" entryId="2GeK838Jag2koOQKmuomYk" />} />

              {/* ye-404 or homepage */}
              <Route
                render={(props: RouteComponentProps<{}>) => {
                  // check if request is coming from locale picker
                  const parseObj = parse(props.location.search, { ignoreQueryPrefix: true });
                  // check if flag is set for NA dataCenter
                  const { match } = props;
                  const isInternationalRedirectOn = enableInternationalRedirect();
                  const formattedUrl = ensureNoTrailingSlash(match.url).toLowerCase();
                  const locale = routeToLocale[formattedUrl];

                  // root path (/) with non default i18n language (like en-CA, fr-CA)
                  const defaultLocaleWithi18nSet = locale?.code && locale.code === DEFAULT_LOCALE.code && DEFAULT_LOCALE.code !== i18n.language;
                  // non default locale url (like /ca, /ca/fr) with different i18n language - to support url override
                  const nonDefaultLocaleWithNoi18n = locale?.code && locale.code !== DEFAULT_LOCALE.code && locale.code !== i18n.language;

                  if (isInternationalRedirectOn && i18n.language !== DEFAULT_LOCALE.code) {
                    if (parseObj?.from === 'localePicker' || defaultLocaleWithi18nSet) {
                      const baseUrl = getBaseUrlFromLocale(i18n.language) || '/';
                      return (<Redirect to={baseUrl} />);
                    } else if (nonDefaultLocaleWithNoi18n) {
                      i18n.changeLanguage(locale.code);
                    }
                  }

                  return <NotFoundPage />;
                }}
              />
            </PagesSwitch>
          </>
        )}
      />
    </Switch>
  );
};
